import {
  format,
  isValid,
  parse,
  parseISO,
} from 'date-fns';
import { fr } from 'date-fns/locale';

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;
const FRENCH_SLASH_DATE = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
const FRENCH_SLASH_DATE_TWO_DIGIT_YEAR = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/;
const FRENCH_SLASH_DATE_SHORT = /^(\d{1,2})\/(\d{1,2})$/;
const FRENCH_DOT_DATE = /(\d{1,2})\.(\d{1,2})\.(\d{4})/;
const FRENCH_DOT_DATE_SHORT = /^(\d{1,2})\.(\d{1,2})$/;
const DATETIME_LOCAL_PATTERN = /^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})/;

const TEXTUAL_DATE_FORMATS = [
  'dd MMM yyyy',
  'd MMM yyyy',
  'dd MMMM yyyy',
  'd MMMM yyyy',
];

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @returns {string}
 */
const toIsoDateString = (year, month, day) => (
  `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
);

/**
 * @param {Date} date
 * @returns {string}
 */
const formatLocalDateToInput = (date) => (
  toIsoDateString(date.getFullYear(), date.getMonth() + 1, date.getDate())
);

/**
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @returns {string}
 */
const isoFromCalendarParts = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  if (
    !isValid(date)
    || date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return '';
  }
  return formatLocalDateToInput(date);
};

/**
 * @param {string | number} twoDigitYear
 * @returns {number}
 */
const expandTwoDigitYear = (twoDigitYear) => {
  const year = Number(twoDigitYear);
  if (year >= 100) return year;
  return year >= 70 ? 1900 + year : 2000 + year;
};

/**
 * French dd/MM[/yyyy] and dd.MM[.yyyy] — always day/month, never US Date.parse.
 * @param {string} trimmed
 * @param {number} fallbackYear
 * @returns {string}
 */
const tryParseFrenchNumericDate = (trimmed, fallbackYear) => {
  const slashTwoDigitYear = trimmed.match(FRENCH_SLASH_DATE_TWO_DIGIT_YEAR);
  if (slashTwoDigitYear) {
    return isoFromCalendarParts(
      expandTwoDigitYear(slashTwoDigitYear[3]),
      Number(slashTwoDigitYear[2]),
      Number(slashTwoDigitYear[1]),
    );
  }

  const slashWithYear = trimmed.match(FRENCH_SLASH_DATE);
  if (slashWithYear) {
    return isoFromCalendarParts(
      Number(slashWithYear[3]),
      Number(slashWithYear[2]),
      Number(slashWithYear[1]),
    );
  }

  const slashShort = trimmed.match(FRENCH_SLASH_DATE_SHORT);
  if (slashShort) {
    return isoFromCalendarParts(
      fallbackYear,
      Number(slashShort[2]),
      Number(slashShort[1]),
    );
  }

  const dotWithYear = trimmed.match(FRENCH_DOT_DATE);
  if (dotWithYear) {
    return isoFromCalendarParts(
      Number(dotWithYear[3]),
      Number(dotWithYear[2]),
      Number(dotWithYear[1]),
    );
  }

  const dotShort = trimmed.match(FRENCH_DOT_DATE_SHORT);
  if (dotShort) {
    return isoFromCalendarParts(
      fallbackYear,
      Number(dotShort[2]),
      Number(dotShort[1]),
    );
  }

  return '';
};

/**
 * API compact YYYYMMDD (e.g. "20260424").
 * @param {string} trimmed
 * @returns {string}
 */
const tryParseCompactApiDate = (trimmed) => {
  if (!/^\d{8}$/.test(trimmed)) return '';

  const year = Number(trimmed.slice(0, 4));
  const month = Number(trimmed.slice(4, 6));
  const day = Number(trimmed.slice(6, 8));
  return isoFromCalendarParts(year, month, day);
};

/**
 * @param {string} trimmed
 * @returns {string}
 */
const normalizeFrenchTextualDate = (trimmed) => (
  trimmed
    .replace(/\baout\b/gi, 'août')
    .replace(/\bdecembre\b/gi, 'décembre')
    .replace(/\bfévrier\b/gi, 'févr.')
    .replace(/\bfevrier\b/gi, 'févr.')
);

const tryParseTextualFrenchDate = (trimmed) => {
  const normalized = normalizeFrenchTextualDate(trimmed.replace(/\.$/, '').trim());
  for (let i = 0; i < TEXTUAL_DATE_FORMATS.length; i += 1) {
    const pattern = TEXTUAL_DATE_FORMATS[i];
    const parsed = parse(normalized, pattern, new Date(), { locale: fr });
    if (isValid(parsed)) {
      return format(parsed, 'yyyy-MM-dd');
    }
  }
  return '';
};

/**
 * Parse a cell/API date string into yyyy-MM-dd for native date inputs.
 * Uses the calendar date from ISO prefixes without timezone shifting (no parseISO on Z-suffixed values).
 * @param {unknown} val - Raw value from the cell or API
 * @returns {string} yyyy-MM-dd or empty string
 */
export const parseCellValueToDateInput = (val) => {
  if (val === null || val === undefined) return '';

  const trimmed = String(val).trim();
  if (!trimmed) return '';

  const isoPrefix = trimmed.match(ISO_DATE_PREFIX);
  if (isoPrefix) {
    return isoPrefix[1];
  }

  const fallbackYear = new Date().getFullYear();
  const frenchNumeric = tryParseFrenchNumericDate(trimmed, fallbackYear);
  if (frenchNumeric) {
    return frenchNumeric;
  }

  const compactApi = tryParseCompactApiDate(trimmed);
  if (compactApi) {
    return compactApi;
  }

  const textual = tryParseTextualFrenchDate(trimmed);
  if (textual) {
    return textual;
  }

  try {
    const date = parseISO(trimmed);
    if (!isValid(date)) return '';
    return formatLocalDateToInput(date);
  } catch {
    return '';
  }
};

/**
 * Parse a cell value into a local calendar Date (no timezone shift).
 * @param {unknown} val
 * @returns {Date | null}
 */
export const parseCellValueToDate = (val) => {
  const iso = parseCellValueToDateInput(val);
  if (!iso) return null;
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Parse a cell/API datetime string into yyyy-MM-dd'T'HH:mm for datetime-local inputs.
 * @param {unknown} val - Raw value from the cell or API
 * @returns {string} datetime-local value or empty string
 */
export const parseCellValueToDatetimeLocalInput = (val) => {
  if (val === null || val === undefined) return '';

  const trimmed = String(val).trim();
  if (!trimmed) return '';

  const datetimeMatch = trimmed.match(DATETIME_LOCAL_PATTERN);
  if (datetimeMatch) {
    const hours = datetimeMatch[2].padStart(2, '0');
    const minutes = datetimeMatch[3];
    return `${datetimeMatch[1]}T${hours}:${minutes}`;
  }

  const isoPrefix = trimmed.match(ISO_DATE_PREFIX);
  if (isoPrefix) {
    return `${isoPrefix[1]}T00:00`;
  }

  const datePart = parseCellValueToDateInput(trimmed);
  if (datePart) {
    return `${datePart}T00:00`;
  }

  try {
    const date = parseISO(trimmed);
    if (!isValid(date)) return '';
    return format(date, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};
